import {
  HorseOption,
  RiderOption,
  RiderResultGroup,
  RiderResultRow,
  ShowSummary,
  SourceShowResult,
} from '../models/RiderResults';

const normalize = (value: string) => value.trim();

const sortAlpha = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' });

const getDateSortValue = (showDateTime: string) => {
  const timestamp = Date.parse(showDateTime);
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const getRiderOptions = (results: SourceShowResult[]): RiderOption[] => {
  const riderMap = new Map<string, SourceShowResult[]>();

  results.forEach((result) => {
    const riderName = normalize(result.riderName);
    if (!riderName) return;
    riderMap.set(riderName, [...(riderMap.get(riderName) || []), result]);
  });

  return Array.from(riderMap.entries())
    .map(([name, riderResults]) => ({
      name,
      horses: Array.from(
        new Set(riderResults.map((result) => normalize(result.horseName)).filter(Boolean)),
      ).sort(sortAlpha),
      resultCount: riderResults.length,
    }))
    .sort((left, right) => sortAlpha(left.name, right.name));
};

const normalizeSelection = (values: string[]) => new Set(values.map(normalize).filter(Boolean));

export const getHorseOptionsForRiders = (results: SourceShowResult[], riderNames: string[]): HorseOption[] => {
  const horseMap = new Map<string, number>();
  const selectedRiders = normalizeSelection(riderNames);

  results.forEach((result) => {
    if (selectedRiders.size > 0 && !selectedRiders.has(normalize(result.riderName))) return;
    const horseName = normalize(result.horseName);
    if (!horseName) return;
    horseMap.set(horseName, (horseMap.get(horseName) || 0) + 1);
  });

  return Array.from(horseMap.entries())
    .map(([name, resultCount]) => ({ name, resultCount }))
    .sort((left, right) => sortAlpha(left.name, right.name));
};

export const getHorseOptionsForRider = (results: SourceShowResult[], riderName: string): HorseOption[] =>
  getHorseOptionsForRiders(results, [riderName]);

export const groupRiderResults = (
  results: SourceShowResult[],
  shows: ShowSummary[],
  riderNames: string[],
  horseNames: string[],
): RiderResultGroup[] => {
  const selectedRiders = normalizeSelection(riderNames);
  const selectedHorses = new Set(horseNames.map(normalize).filter(Boolean));
  const showMap = new Map(shows.map((show) => [show.showId, show]));
  const groupMap = new Map<string, RiderResultGroup>();

  results
    .filter((result) => selectedRiders.size === 0 || selectedRiders.has(normalize(result.riderName)))
    .filter((result) => selectedHorses.size === 0 || selectedHorses.has(normalize(result.horseName)))
    .forEach((result) => {
      const show = showMap.get(result.showId);
      const showName = show?.showName || result.showId || 'Unknown Show';
      const showDateTime = show?.showDateTime || '';
      const row: RiderResultRow = {
        ...result,
        riderName: normalize(result.riderName),
        horseName: normalize(result.horseName),
        division: normalize(result.division),
        time: normalize(result.time),
        showName,
        showDateTime,
      };

      const group = groupMap.get(result.showId) || {
        showId: result.showId,
        showName,
        showDateTime,
        results: [],
      };
      group.results.push(row);
      groupMap.set(result.showId, group);
    });

  return Array.from(groupMap.values()).sort((left, right) => {
    const leftDate = getDateSortValue(left.showDateTime);
    const rightDate = getDateSortValue(right.showDateTime);

    if (leftDate !== null && rightDate !== null && leftDate !== rightDate) {
      return rightDate - leftDate;
    }
    if (leftDate !== null && rightDate === null) return -1;
    if (rightDate !== null && leftDate === null) return 1;
    return sortAlpha(left.showName, right.showName);
  });
};
